import { Link } from 'react-router-dom';
import { Mail, Shield, Clock, FileX } from 'lucide-react';

/**
 * Page publique de demande de suppression de compte.
 * Exposée sur propilote.ca/delete-account.
 * Requise par Google Play Data Safety (URL de suppression de compte).
 * Doit documenter clairement : procédure, données supprimées, conservation.
 */
const DeleteAccountPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm" data-testid="delete-back-home">
            ← Retour
          </Link>
        </div>

        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3" data-testid="delete-title">
          <FileX className="text-red-500" size={40} />
          Suppression de compte ProPilote Driver
        </h1>
        <p className="text-slate-400 mb-8">
          Cette page explique comment demander la suppression de votre compte et de vos données personnelles associées à l'application ProPilote Driver.
        </p>

        <section className="bg-slate-900 rounded-lg p-6 mb-6 border border-slate-800">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <Mail className="text-blue-400" size={24} />
            Procédure pour demander la suppression
          </h2>

          <div className="bg-emerald-900/20 border border-emerald-700 rounded p-4 mb-6">
            <h3 className="text-lg font-semibold text-emerald-400 mb-2">Option 1 (recommandée) — Directement dans l'application</h3>
            <p className="mb-2">La méthode la plus rapide et immédiate :</p>
            <ol className="list-decimal pl-6 space-y-1 text-slate-300">
              <li>Ouvrez l'application <strong>ProPilote Driver</strong> sur votre téléphone</li>
              <li>Rendez-vous sur l'onglet <strong>Profil</strong></li>
              <li>Faites défiler jusqu'en bas de la page</li>
              <li>Appuyez sur <strong>« Supprimer mon compte »</strong></li>
              <li>Confirmez la case « Je comprends que cette action est irréversible »</li>
              <li>Appuyez sur <strong>« Supprimer définitivement mon compte »</strong></li>
            </ol>
            <p className="mt-3 text-sm text-emerald-300">
              La suppression est <strong>immédiate</strong> — toutes vos données sont effacées de nos serveurs.
            </p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700 rounded p-4">
            <h3 className="text-lg font-semibold mb-2">Option 2 — Par courriel</h3>
            <p className="mb-3">
              Si vous n'avez plus accès à l'application, envoyez un courriel à :
            </p>
            <div className="bg-slate-800 rounded p-3 mb-3">
              <a
                href="mailto:apps@expresspatmorin.ca?subject=Demande%20de%20suppression%20de%20compte%20ProPilote"
                className="text-blue-400 hover:text-blue-300 text-lg font-mono"
                data-testid="delete-email-link"
              >
                apps@expresspatmorin.ca
              </a>
            </div>
            <p className="mb-1"><strong>Objet :</strong> Demande de suppression de compte ProPilote</p>
            <p className="mb-2"><strong>À inclure dans le courriel :</strong></p>
            <ul className="list-disc pl-6 space-y-1 text-slate-300">
              <li>Adresse e-mail utilisée pour votre compte ProPilote</li>
              <li>Nom complet du chauffeur ou de l'entreprise</li>
              <li>Confirmation explicite de votre demande de suppression</li>
            </ul>
          </div>

          <p className="mt-4 text-sm text-slate-400">
            Note : si vous êtes chauffeur employé par une entreprise cliente de ProPilote, votre compte est géré par votre administrateur de flotte. Contactez d'abord votre employeur, ou écrivez-nous directement en précisant la situation.
          </p>
        </section>

        <section className="bg-slate-900 rounded-lg p-6 mb-6 border border-slate-800">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <Shield className="text-emerald-400" size={24} />
            Données supprimées
          </h2>
          <p className="mb-3">Lors de la suppression, les données suivantes sont effacées de nos systèmes :</p>
          <ul className="list-disc pl-6 space-y-1 text-slate-300">
            <li>Informations de compte (nom, courriel, téléphone, mot de passe)</li>
            <li>Historique de positions GPS</li>
            <li>Rapports journaliers créés par le chauffeur</li>
            <li>Scans de bons de livraison (BOL) et photos associées</li>
            <li>Signatures clients enregistrées</li>
            <li>Heures de service et notes personnelles</li>
            <li>Préférences et paramètres de l'application</li>
          </ul>
        </section>

        <section className="bg-slate-900 rounded-lg p-6 mb-6 border border-slate-800">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <Clock className="text-amber-400" size={24} />
            Données conservées et délais
          </h2>
          <p className="mb-3">
            Certaines données peuvent être conservées après la suppression du compte pour des raisons légales, fiscales ou de sécurité :
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-300">
            <li>
              <strong>Registres légaux SAAQ / heures de service</strong> — conservés jusqu'à <strong>6 mois</strong> après la fin d'emploi conformément aux exigences de la SAAQ Québec.
            </li>
            <li>
              <strong>Factures et documents comptables</strong> — conservés <strong>7 ans</strong> conformément aux lois fiscales canadiennes (Loi de l'impôt sur le revenu).
            </li>
            <li>
              <strong>Logs de sécurité anonymisés</strong> — conservés <strong>90 jours</strong> puis effacés automatiquement.
            </li>
          </ul>
          <p className="mt-4">
            <strong>Délai de traitement de la demande :</strong> nous traitons chaque demande de suppression sous <strong>30 jours ouvrables</strong> maximum. Vous recevrez un e-mail de confirmation une fois la suppression effectuée.
          </p>
        </section>

        <section className="bg-slate-900 rounded-lg p-6 mb-6 border border-slate-800">
          <h2 className="text-2xl font-semibold mb-4">Suppression partielle</h2>
          <p>
            Si vous ne souhaitez pas supprimer complètement votre compte mais uniquement certaines données (par exemple votre historique GPS ou vos anciens rapports), précisez-le dans votre courriel. Nous vous aiderons à effacer uniquement les données ciblées.
          </p>
        </section>

        <section className="bg-slate-900 rounded-lg p-6 border border-slate-800">
          <h2 className="text-2xl font-semibold mb-4">Contact et questions</h2>
          <p className="mb-2">
            Pour toute question sur cette procédure :
          </p>
          <ul className="list-none pl-0 space-y-1 text-slate-300">
            <li>Courriel : <a href="mailto:apps@expresspatmorin.ca" className="text-blue-400 hover:text-blue-300">apps@expresspatmorin.ca</a></li>
            <li>Site web : <a href="https://propilote.ca" className="text-blue-400 hover:text-blue-300">https://propilote.ca</a></li>
            <li>Politique de confidentialité : <Link to="/privacy" className="text-blue-400 hover:text-blue-300">/privacy</Link></li>
          </ul>
        </section>

        <p className="text-center text-slate-500 text-sm mt-8">
          Dernière mise à jour : février 2026
        </p>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
